import React, { useState, useEffect, useRef } from 'react';
import { IoCloseOutline } from 'react-icons/io5';
import { USAGE_OPTIONS, OTHER_PREFIX, OTHER_MAX } from '../utils/requestGroups';

/**
 * #9 (rework) — Download-request capture form.
 *
 * A reusable modal shown before a download request is submitted. The form is
 * MANDATORY (client decision, 2026-08): every request must carry a project
 * name, a project brief and at least one usage channel — usage drives the
 * approve/reject/counter decision (event footage is often restricted for
 * third-party social media and TV usage). Send stays disabled until the form
 * is valid; the BE re-validates and 400s regardless.
 *
 * The parent owns the actual submit: `onSubmit({projectName, projectBrief,
 * usageTypes})` runs the request; the parent then closes the modal (by
 * flipping `open`). One set of values applies to the whole submit (all videos
 * in that request).
 *
 * Props:
 *   open      – whether the modal is shown
 *   count     – number of videos in this request (for the header)
 *   onCancel  – close without submitting
 *   onSubmit  – async ({projectName, projectBrief, usageTypes}) => void ;
 *               parent performs the POST. usageTypes is a string array; a
 *               filled "Other" entry arrives as "Other: <text>".
 */
const MAX_PROJECT_NAME = 60;   // BE enforces the same cap
const MAX_PROJECT_BRIEF = 1000;


// usage_types is stored comma-joined: the free text must not carry commas
// or line breaks (the BE normalises too).
const normaliseOther = (text) => text.replace(/[,\r\n]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, OTHER_MAX);

export default function DownloadRequestFormModal({ open, count = 0, onCancel, onSubmit }) {
    const [projectName, setProjectName] = useState('');
    const [projectBrief, setProjectBrief] = useState('');
    const [selectedUsages, setSelectedUsages] = useState([]);
    const [otherChecked, setOtherChecked] = useState(false);
    const [otherText, setOtherText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const otherInputRef = useRef(null);

    // Reset each time the modal opens.
    useEffect(() => {
        if (open) {
            setProjectName('');
            setProjectBrief('');
            setSelectedUsages([]);
            setOtherChecked(false);
            setOtherText('');
            setSubmitting(false);
        }
    }, [open]);

    // Esc closes (unless a submit is in flight) — matches the requester popup (#13).
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape' && !submitting) onCancel(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, submitting, onCancel]);

    // "Other" reveals its text box below the fold of a scrolled body: bring it
    // into view and focus it so the user sees where to type.
    useEffect(() => {
        if (otherChecked && otherInputRef.current) {
            const el = otherInputRef.current;
            if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            el.focus();
        }
    }, [otherChecked]);

    if (!open) return null;

    // "Other" only counts as a usage once its text is filled in.
    const otherClean = normaliseOther(otherText);
    const usageValid = selectedUsages.length > 0 || (otherChecked && otherClean.length > 0);
    const otherIncomplete = otherChecked && otherClean.length === 0;
    const formValid = projectName.trim().length > 0
        && projectBrief.trim().length > 0
        && usageValid
        && !otherIncomplete;

    const toggleUsage = (usage) => {
        setSelectedUsages((prev) => (
            prev.includes(usage) ? prev.filter((u) => u !== usage) : [...prev, usage]
        ));
    };

    const handleSend = async () => {
        if (submitting || !formValid) return;
        setSubmitting(true);
        try {
            const usageTypes = USAGE_OPTIONS.filter((u) => selectedUsages.includes(u));
            if (otherChecked && otherClean) usageTypes.push(`${OTHER_PREFIX}${otherClean}`);
            await onSubmit({
                projectName: projectName.trim(),
                projectBrief: projectBrief.trim(),
                usageTypes,
            });
        } catch (e) {
            // parent handles/logs errors; re-enable so the user can retry or cancel
            setSubmitting(false);
        }
    };

    const labelStyle = { fontSize: '13px', fontWeight: 600 };
    const requiredMark = <span style={{ color: '#c33' }}>*</span>;

    return (
        <div className="modal-admin-wrapper">
            <div className="modal-ad-popup modal-md">
                <div className="modal-innerpopup">
                    <span className="closeBtn">
                        <IoCloseOutline size={32} onClick={() => { if (!submitting) onCancel(); }} />
                    </span>
                    <div className="modal-headdiv d-flex justify-content-between">
                        <h5 className="modal-head">
                            Download Request{count > 1 ? ` — ${count} files` : ''}
                        </h5>
                    </div>
                    <div className="modal-admin-body my-3 text-start">
                        <p className="form-hint mb-3">
                            Tell the approver what the content is for. Requests are reviewed per project.
                        </p>

                        <label htmlFor="request-project-name" className="mb-1 d-block" style={labelStyle}>
                            Project name {requiredMark}
                        </label>
                        <input
                            id="request-project-name"
                            type="text"
                            className="form-control"
                            maxLength={MAX_PROJECT_NAME}
                            placeholder="What is the requested content for? e.g. Winter campaign 2026"
                            value={projectName}
                            disabled={submitting}
                            onChange={(e) => setProjectName(e.target.value)}
                            // eslint-disable-next-line jsx-a11y/no-autofocus
                            autoFocus
                        />

                        <label htmlFor="request-project-brief" className="mb-1 mt-3 d-block" style={labelStyle}>
                            Project brief / purpose {requiredMark}
                        </label>
                        <textarea
                            id="request-project-brief"
                            className="form-control"
                            rows={4}
                            maxLength={MAX_PROJECT_BRIEF}
                            placeholder="Tell the approver about the project — why the content is requested and how it will be used."
                            value={projectBrief}
                            disabled={submitting}
                            onChange={(e) => setProjectBrief(e.target.value)}
                        />
                        <div className="text-end mt-1 form-hint">
                            {projectBrief.length}/{MAX_PROJECT_BRIEF}
                        </div>

                        <div className="mb-1 mt-2 d-block" style={labelStyle}>
                            Usage {requiredMark}
                            <span className="form-hint" style={{ fontWeight: 400 }}> — where will the content appear? Select all that apply.</span>
                        </div>
                        <div className="usage-pills" role="group" aria-label="Usage">
                            {USAGE_OPTIONS.map((usage) => {
                                const on = selectedUsages.includes(usage);
                                return (
                                    <button
                                        key={usage}
                                        type="button"
                                        className={`usage-pill ${on ? 'active' : ''}`}
                                        aria-pressed={on}
                                        disabled={submitting}
                                        onClick={() => toggleUsage(usage)}
                                    >
                                        {usage}
                                    </button>
                                );
                            })}
                            <button
                                type="button"
                                className={`usage-pill ${otherChecked ? 'active' : ''}`}
                                aria-pressed={otherChecked}
                                disabled={submitting}
                                onClick={() => setOtherChecked((prev) => !prev)}
                            >
                                Other
                            </button>
                        </div>
                        {otherChecked && (
                            <div className="usage-other-wrap">
                                <input
                                    ref={otherInputRef}
                                    type="text"
                                    className="form-control"
                                    maxLength={OTHER_MAX}
                                    placeholder="Describe the usage"
                                    aria-label="Other usage"
                                    value={otherText}
                                    disabled={submitting}
                                    onChange={(e) => setOtherText(e.target.value)}
                                />
                                <div className="d-flex justify-content-between form-hint mt-1">
                                    <span>Short description, no commas.</span>
                                    <span>{otherText.length}/{OTHER_MAX}</span>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="d-flex justify-content-end gap-2">
                        <button
                            type="button"
                            className="accept-all-btn py-2 accept-all-gray"
                            disabled={submitting}
                            onClick={() => onCancel()}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="accept-all-btn py-2 d-flex align-items-center gap-2 justify-content-center"
                            disabled={submitting || !formValid}
                            onClick={handleSend}
                        >
                            {submitting ? 'Sending…' : 'Send request'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
